import * as BABYLON from '@babylonjs/core';
import * as GUI from '@babylonjs/gui';
import StarsRenderer from '../rendering/stars.js'

import { BACKEND_URL } from '../constants.js';
import STATIC_DATASET_URL from '../assets/data/data-stars.msgpack'

import { state, data_vis_parameters } from '../App.js'
import getshadermat_unlit from '../rendering/shaders/unlit.js'
import starTexPath from '../assets/textures/star.png';
import AnimatedStar from '../anim/animated-star.js';
import StarFieldInput from '../input/starfield-input.js'
import StarVisualizationData from '../star-visualization-data.js';
import GuiTextBlockManaged from '../gui/gui-text-block-managed.js';
import { DistanceUnits } from '../components/DrawerList.js';
import { toEngineeringNotationReadable } from '../util/str-util.js'

import { decode } from '@msgpack/msgpack'
import { raiseEvent } from '../util/Events.ts';
import { loadExoplanetsData } from '../dataprocessing/data-exoplanets.ts';

const AsyncOpState = {
    DISPATCHED: 1,
    IDLE: 2
}

const localState = {
    frameRenderingStatus: AsyncOpState.IDLE,
    rotationSettingStatus: AsyncOpState.IDLE,
    loadStarsDone: false,
    readyToReceivePointerDown: true,
    numStarsShown: 0,
    lineInstance: undefined,
    scene: undefined,
    camera: undefined,
    engine: undefined
}

const starsDataPerInstance = {
}

const textLabelsData = {}
const selectionsData = {
    selectedStar: -1,
    isSelectionFixed: false
}

let starsRawData = undefined

export function createScene(engine, canvas, onTabKeyLambda) {
    //Init scene
    const scene = new BABYLON.Scene(engine);
    scene.clearColor = new BABYLON.Color3(0.0, 0.0, 0.0);

    //Init camera
    const camera = new BABYLON.ArcRotateCamera("camera1", 0, 0, 10, BABYLON.Vector3.Zero(), scene);
    camera.lowerRadiusLimit = 10;
    camera.upperRadiusLimit = 18000;
    camera.wheelDeltaPercentage = 0.03;
    camera.maxZ = 50000;
    camera.attachControl(canvas, true);

    //Init UI parent
    var fullscreenUi = GUI.AdvancedDynamicTexture.CreateFullscreenUI("UI", true, scene);

    //Init local state
    localState.fullscreenUi = fullscreenUi;
    localState.camera = camera;
    localState.scene = scene;
    localState.engine = engine;

    //Init overlay UI
    initText(scene, fullscreenUi);
    initSystemSummaryOverlayUi(scene, fullscreenUi);

    //Init thin instance template object
    let starSize = 3;
    var starsThinInstanceObj = BABYLON.MeshBuilder.CreatePlane("plane", {
        width: starSize,
        height: starSize
    }, scene);

    //Define star material
    let unlit = new BABYLON.StandardMaterial('unlit', scene);//getshadermat_unlit(scene);
    let starTexture = new BABYLON.Texture(starTexPath, scene);
    starTexture.hasAlpha = true;
    unlit.diffuseTexture = starTexture;
    unlit.backFaceCulling = false;
    unlit.disableLighting = true;
    unlit.emissiveColor = BABYLON.Color3.White();
    unlit.useAlphaFromDiffuseTexture = true
    starsThinInstanceObj.material = unlit;

    //Init stars data
    let starPositions;
    let numStars = 0;
    fetch_stars_data().then((data) => {
        if (!data) data = dummy_stars_data();

        starsRawData = data;

        numStars = Math.min(50000, data.length);
        starPositions = new Array(numStars);
        //instances = new Array(numStars);

        starsDataPerInstance.starPositions = starPositions;
        starsDataPerInstance.positionMatrices = new Array(numStars);
        starsDataPerInstance.bufferMatrices = new Float32Array(16 * numStars);
        starsDataPerInstance.bufferColors = new Float32Array(4 * numStars)
        starsDataPerInstance.originalObject = starsThinInstanceObj;
        starsDataPerInstance.starNames = new Array(numStars);
        starsDataPerInstance.animData = new Array(numStars);
        starsDataPerInstance.dataVis = new Array(numStars);

        create_star_instances(numStars, starPositions, starsThinInstanceObj, data);

        starsThinInstanceObj.thinInstanceSetBuffer("matrix", starsDataPerInstance.bufferMatrices, 16, false);
        starsThinInstanceObj.thinInstanceSetBuffer("color", starsDataPerInstance.bufferColors, 4, false);

        //starsDataPerInstance.instances = instances;

        starsRenderer.updatePositions(starPositions, engine);

        localState.loadStarsDone = true;
    });

    //Init stars renderer
    let starsRenderer = new StarsRenderer();
    starsRenderer.init(scene, engine, camera, canvas);

    //Init distance indicator line
    const options = {
        points: [BABYLON.Vector3.Zero(), new BABYLON.Vector3(1, 0, 0)], //vec3 array,
        updatable: true
    }
    localState.lineInstance = BABYLON.MeshBuilder.CreateDashedLines("dashedline", options, scene);
    localState.lineInstance.visibility = 0;

    //Init input handling

    scene.onKeyboardObservable.add((kbInfo) => {
        switch (kbInfo.type) {
            case BABYLON.KeyboardEventTypes.KEYDOWN:
                switch (kbInfo.event.key) {
                    case "Shift":
                        state.showStars = true
                        break;
                }
                break;
            case BABYLON.KeyboardEventTypes.KEYUP:
                switch (kbInfo.event.key) {
                    case "Shift":
                        state.showStars = false
                        break;
                }
                break;
        }
    });

    localState.inputManager = new StarFieldInput(scene, {
        onClickLambda: () => {
            if (selectionsData.isSelectionFixed) {
                selectionsData.isSelectionFixed = false;
                onDeselectStar(selectionsData.selectedStar);
            }
            else {
                if (selectionsData.selectedStar >= 0) {
                    selectionsData.isSelectionFixed = true;
                    onSelectStar(selectionsData.selectedStar);
                }
            }
        },
        onTabKeyLambda: () => {
            //state.onTabKeyLambda();
        }
    })

    scene.onPointerObservable.add((pointerInfo) => {
        localState.inputManager.onPointerObservable(pointerInfo);
    });

    scene.onKeyboardObservable.add((kbInfo) => {
        localState.inputManager.onKeyboardObservable(kbInfo);
    })

    //Main Render loop
    scene.onBeforeRenderObservable.add(() => {
        if (localState.loadStarsDone) {
            localState.numStarsShown = 0;
            for (let i = 0; i < numStars; i++) {
                starsDataPerInstance.dataVis[i].update(data_vis_parameters);
                if (starsDataPerInstance.dataVis[i].isFilterMatch)
                    localState.numStarsShown++;
            }

            updateDynamicLabels(scene, camera, fullscreenUi);
            updateBufferMatrices(starsDataPerInstance.bufferMatrices, numStars, camera, engine.getDeltaTime() / 1000);
            updateStarColors(starsDataPerInstance.bufferColors, numStars)


            UpdateDashedLineLabelPosition(selectionsData.selectedStar);
        }

        let n = starsDataPerInstance.starSpinAnimsProgress ? starsDataPerInstance.starSpinAnimsProgress[0] : 0;
        state.dbgStr = `Showing ${localState.numStarsShown} stars out of ${numStars}`;

        if (selectionsData.selectedStar >= 0) {
            updateSummaryPanelPosition(selectionsData.selectedStar)
        }
    });

    return scene;
}

function initSystemSummaryOverlayUi(scene, fullscreenUi) {

}

const desiredDashesPerDist = 2;

function updateSummaryPanelPosition(hoveredStarIndex) {
    let starPos = starsDataPerInstance.starPositions[hoveredStarIndex];

    const positionSS = WorldToScreenPoint(starPos);
    raiseEvent('starSelectionChanged', {
        starName: starsRawData[hoveredStarIndex]['hostname'],
        positionSS: { x: positionSS.x, y: positionSS.y }
    })
}

//Called before selectionsData.selectedStar is updated to a new value
function onHoveredStarChanged(prevIndex, newIndex) {
    let starPos = starsDataPerInstance.starPositions[newIndex];
    let points = [BABYLON.Vector3.Zero(), starPos]
    let pathLength = BABYLON.Vector3.Distance(points[0], points[1]);
    const options = {
        points: points,
        updatable: true,
        dashNb: desiredDashesPerDist * pathLength,
        //dashSize: 3,
        //gapSize: 2
    }
    if (localState.lineInstance) {
        localState.lineInstance.dispose();
    }
    localState.lineInstance = BABYLON.MeshBuilder.CreateDashedLines("dashedline", options, state.scene);
    localState.lineInstance.color = BABYLON.Color3.White();

    const labelOffset = 0;

    const midpoint = (points[0].add(points[1])).multiplyByFloats(0.5, 0.5, 0.5);
    const dir1 = (points[1].subtract(points[0])).normalize();
    const dir2 = (midpoint.subtract(localState.camera.position)).normalize();
    const normalDir = BABYLON.Vector3.Cross(dir1, dir2);
    const labelPos = midpoint.add(normalDir.multiplyByFloats(labelOffset, labelOffset, labelOffset));

    UpdateDashedLineLabelPosition(newIndex);

    updateSummaryPanelPosition(newIndex)
}
const clamp = (num, min, max) => Math.min(Math.max(num, min), max)
function WorldToScreenPoint(positionWS) {
    const scene = localState.scene;
    const canvas = localState.engine.getRenderingCanvas();
    var positionSS = BABYLON.Vector3.Project(
        positionWS,
        BABYLON.Matrix.Identity(),
        scene.getTransformMatrix(),
        { x: 0, y: 0, width: canvas.width, height: canvas.height });

    return positionSS;
}

function Midpoint(vec0, vec1) {
    return vec0.add(vec1).multiplyByFloats(0.5, 0.5, 0.5);
}

function onSelectStar(starIndex) {
    //console.log(`onSelectStar ${starIndex}`)
}

function onDeselectStar(starIndex) {
    //console.log(`onDeselectStar ${starIndex}`)
    //localState.lineInstance.visibility = 0;
}

function UpdateDashedLineLabelPosition(selectedStarIndex) {
    let starPos = starsDataPerInstance.starPositions[selectedStarIndex];
    let points = [BABYLON.Vector3.Zero(), starPos]
    let sy_dist = Number.parseFloat(starsDataPerInstance.dataVis[selectedStarIndex].row.sy_dist);

    const ss0 = WorldToScreenPoint(points[0]);
    let ss1 = WorldToScreenPoint(points[1]);
    let canvas = localState.engine.getRenderingCanvas();
    //TODO: Automatically detect out-of-bounds SS points and backtrack them along the line segment until they hit the screen boundary.
    //Make a utility function for this
    const ssMidpoint = Midpoint(ss0, ss1);

    switch (state.distanceUnitsChosen) {
        case DistanceUnits.PARSECS:
            textLabelsData.dashedLineLabel.updateText(`${sy_dist.toFixed(1)} pc`);
            break;
        case DistanceUnits.LIGHT_YEARS:
            const dist_lyr = sy_dist * 3.26156;
            textLabelsData.dashedLineLabel.updateText(`${dist_lyr.toFixed(1)} lyr`);
            break;
        case DistanceUnits.AU:
            const dist_au = sy_dist * 206265;
            textLabelsData.dashedLineLabel.updateText(toEngineeringNotationReadable(dist_au, "AU"));
            break;
    }
    textLabelsData.dashedLineLabel.updatePosition(ssMidpoint);
    textLabelsData.dashedLineLabel.updateColor(BABYLON.Color3.White(), 1);
}

//Updates the buffer that drives star instance transforms
function updateBufferMatrices(buffer, numStars, camera, deltaTime) {
    if (!starsDataPerInstance.originalObject) return;

    //Rotates the mesh to match camera orientation
    const rot = BABYLON.Quaternion.FromEulerAngles(Math.PI / 2, Math.PI / 2, 0)
        .multiply(BABYLON.Quaternion.FromEulerAngles(0, 0, camera.alpha))
        .multiply(BABYLON.Quaternion.FromEulerAngles(camera.beta, 0, 0));
    let rotationMatrix = new BABYLON.Matrix();
    rot.toRotationMatrix(rotationMatrix);

    for (let i = 0; i < numStars; i++) {
        let starSelected = selectionsData.selectedStar == i;
        starsDataPerInstance.animData[i].doDelta(deltaTime, starSelected);

        //Transforms are last declared -> first applied (I think)
        let matrix = rotationMatrix.multiply(starsDataPerInstance.positionMatrices[i]); //Position and camera-facing
        matrix = starsDataPerInstance.animData[i].applyTransformToMatrix(matrix); //Animation Transform
        matrix = starsDataPerInstance.dataVis[i].applyScalingTransform(matrix); //Data Visualization Transform

        let distanceFromCamera = BABYLON.Vector3.Distance(starsDataPerInstance.starPositions[i], camera.position);
        //distanceFromCamera = Math.max(distanceFromCamera, state.perspectiveMinDistThreshold);
        const minDist = 5;
        const additionalScaling = state.starScale;//lerp(1 / (minDist + 1), 1,state.perspectiveFactor);
        let scaleFactorFromDist = additionalScaling * distanceFromCamera / Math.pow(Math.max(minDist, distanceFromCamera), state.perspectiveFactor);
        //scaleFactorFromDist = Math.min(scaleFactorFromDist, 1);
        matrix = BABYLON.Matrix.Scaling(scaleFactorFromDist, scaleFactorFromDist, scaleFactorFromDist).multiply(matrix);

        buffer.set(matrix.m, i * 16);
    }
    starsDataPerInstance.originalObject.thinInstanceBufferUpdated("matrix");
}

function lerp(a, b, t) {
    return a + t * (b - a);
}

function updateStarColors(buffer, numStars) {
    if (!starsDataPerInstance.originalObject) return;

    for (let i = 0; i < numStars; i++) {
        starsDataPerInstance.dataVis[i].applyColor(buffer, i);
    }
    starsDataPerInstance.originalObject.thinInstanceBufferUpdated("color");
}

//Iterates through the star data and spawns instances to be displayed. All relevant outputs are fields in starsInstanceData
async function create_star_instances(numStars, starPositions, plane, data) {
    let instanceIndex = 0;
    data.forEach((row, index) => {
        if (index >= numStars) return;

        if (row.sy_dist || true) {
            const parsecs_to_ws = 1;
            const distWS = row.sy_dist * parsecs_to_ws;

            const lonRad = BABYLON.Tools.ToRadians(row.glon);
            const latRad = BABYLON.Tools.ToRadians(row.glat);

            const x = -distWS * Math.cos(latRad) * Math.sin(lonRad);
            const y = distWS * Math.sin(latRad);
            const z = distWS * Math.cos(latRad) * Math.cos(lonRad);

            const starPosition = new BABYLON.Vector3(x, y, z);
            starPositions[instanceIndex] = starPosition;
            starsDataPerInstance.animData[instanceIndex] = new AnimatedStar();

            /*createStar(scene, fullscreenUi, {
                position: starPosition,
                name: row.hostname
            })*/
            var matrix = BABYLON.Matrix.Translation(starPosition.x, starPosition.y, starPosition.z);
            starsDataPerInstance.positionMatrices[instanceIndex] = matrix;

            matrix.copyToArray(starsDataPerInstance.bufferMatrices, instanceIndex * 16);

            //let color = calculateStarColor(row);
            const starsAlpha = .7;
            //starsDataPerInstance.bufferColors.set([color.r, color.g, color.b, starsAlpha], instanceIndex * 4);

            starsDataPerInstance.starNames[instanceIndex] = row.hostname;

            starsDataPerInstance.dataVis[instanceIndex] = new StarVisualizationData(row);

            instanceIndex++;
        }
    });
}

function calculateStarColor(row) {
    let color = BABYLON.Color3.White();

    if (row.st_spectype == "") {
        color = BABYLON.Color3.Red();
    } else {
        color = BABYLON.Color3.White();
    }
    color = get_stellar_color(row.st_spectype);

    return color;
}

//Translate spectral type string into a BABYLON.Color3
function get_stellar_color(st_spectype) {
    const match = st_spectype.match(/^([A-Za-z0-9]+)/);
    if (match) {
        const colorMap = {
            'O': '#9bb0ff',
            'B': '#aabfff',
            'A': '#cad7ff',
            'F': '#f8f7ff',
            'G': '#fff4ea',
            'K': '#ffd2a1',
            'M': '#ffcc6f'
        };

        const color = colorMap[st_spectype.charAt(0)] || '#888888';
        return BABYLON.Color3.FromHexString(color);
    }
    else {
        return BABYLON.Color3.Purple();
        const mul = .1;
        return BABYLON.Color3.Gray().multiplyByFloats(mul, mul, mul);//.multiply(1);
    }
}

const LOAD_FROM = {
    BACKEND: 0,
    STATIC: 1
}

//Retrieve stars data from the backend and reformat
async function fetch_stars_data() {
    try {
        let data;

        const loadFromMode = LOAD_FROM.STATIC;
        if (loadFromMode == LOAD_FROM.BACKEND) {
            const response = await fetch(BACKEND_URL);
            data = await response.json();
        }
        else {
            const response = await fetch(STATIC_DATASET_URL);
            const arrayBuffer = await response.arrayBuffer();
            data = decode(new Uint8Array(arrayBuffer));
        }

        const { columns, rows } = data;

        // Convert rows into an array of objects with column names as keys
        const formattedData = rows.map(row => {
            const rowObject = {};
            columns.forEach((column, index) => {
                rowObject[column] = row[index];
            });
            return rowObject;
        });
        formattedData.unshift(getRowData_sol())

        return formattedData;
    } catch (error) {
        console.error('Error fetching data:', error);
    }
}

//Initialize the text labels for the scene
function initText(scene, fullscreenUi) {
    textLabelsData.dynamicLabelInstances = []
    const num_labels = 1;
    for (let i = 0; i < num_labels; i++) {
        let ret = createLabel(scene, fullscreenUi, {
            position: BABYLON.Vector3.Zero(),
            text: ""
        });
        textLabelsData.dynamicLabelInstances.push(ret);
    }
    console.log(`Created ${num_labels} dynamic star labels.`);

    textLabelsData.homeSystemLabel = createLabel(scene, fullscreenUi, {
        position: BABYLON.Vector3.Zero(),
        text: "Sol"
    });
    //textLabelsData.homeSystemLabel.textBlock.linkOffsetX = 50;

    textLabelsData.dashedLineLabel = new GuiTextBlockManaged(fullscreenUi, localState.engine.getRenderingCanvas());
}

/*
function createStar(scene, fullscreenUi, { position, name }) {
    const linkMesh = BABYLON.MeshBuilder.CreateSphere('linkMesh', { diameter: 0.1 }, scene);
    linkMesh.position = position;

    var image = new GUI.Image();
    fullscreenUi.addControl(image); //Must be attached before specifying source, to avoid crash
    image.source = img_star;

    image.width = "50px";
    image.height = "50px";

    image.linkWithMesh(linkMesh);

    return;
    createLabel(scene, fullscreenUi, {
        position: position,
        text: name
    });
}*/

//Creates a floating text label to the right side of the position.
//labelMesh: Invisible mesh that governs the label position
//textBlock: The component that renders the text
function createLabel(scene, fullscreenUi, { position, text }) {
    const labelMesh = BABYLON.MeshBuilder.CreateSphere('labelMesh', { diameter: 0.001 }, scene);
    labelMesh.position = position;

    var textBlock = new GUI.TextBlock();
    textBlock.text = text;
    textBlock.color = "orange";
    textBlock.fontSize = 17;
    textBlock.width = '300px'

    var textWidth = fullscreenUi.getContext().measureText(textBlock.text).width;
    textBlock.width = textWidth;

    const base_link_offset = 0; //30?
    const textWidthScaleFactor = 0;
    textBlock.linkOffsetX = base_link_offset + (textWidth * textWidthScaleFactor / 2.0);

    fullscreenUi.addControl(textBlock);
    textBlock.linkWithMesh(labelMesh);

    return {
        labelMesh,
        textBlock
    };
}

function updateLabel(fullscreenUi, { labelMesh, textBlock }, { position, text, alpha, color }) {
    labelMesh.position = position;
    textBlock.text = text;

    color = color || BABYLON.Color3.FromHexString("#FFA500");
    color.a = alpha;
    textBlock.color = color.toHexString();
    textBlock.alpha = alpha;

    var textWidth = fullscreenUi.getContext().measureText(textBlock.text).width;
    textBlock.width = textWidth;

    const base_link_offset = 30;
    textBlock.linkOffsetX = base_link_offset + (textWidth / 2.0);
    textBlock.linkOffsetY = -40
}

//Also handles selection. TODO: Clarify naming structure
function updateDynamicLabels(scene, camera, fullscreenUi) {
    if (selectionsData.isSelectionFixed) return;

    var ray = scene.createPickingRay(scene.pointerX, scene.pointerY, BABYLON.Matrix.Identity(), camera, false);

    let matches = [];
    const maxMatches = textLabelsData.dynamicLabelInstances.length;
    starsDataPerInstance.starPositions.forEach((point, index) => {
        let angle = computeAngularDistance(ray, point);
        matches.push({ angle, index })
        matches.sort((a, b) => a.angle - b.angle);
        if (matches.length > maxMatches) {
            matches.pop();
        }
    });

    let newSelection = matches[0].index;
    if (newSelection != selectionsData.selectedStar) {
        onHoveredStarChanged(selectionsData.selectedStar, newSelection);
    }
    selectionsData.selectedStar = newSelection;

    for (let i = 0; i < matches.length; i++) {
        const index = matches[i].index;
        let alpha = 1 - inverselerp(matches[i].angle, 0.01, 0.05);
        alpha = Math.min(1, Math.max(alpha, 0));

        alpha = 1
        raiseEvent("updateStarSystemLabelAlpha", alpha)
        alpha = 0

        const distanceFromCamera = BABYLON.Vector3.Distance(starsDataPerInstance.starPositions[index], camera.position);
        updateLabel(fullscreenUi, textLabelsData.dynamicLabelInstances[i], {
            position: starsDataPerInstance.starPositions[index],
            text: starsDataPerInstance.starNames[index],
            alpha: alpha
        });

        updateLabel(fullscreenUi, textLabelsData.homeSystemLabel, {
            position: BABYLON.Vector3.Zero(),
            text: "Sol",
            alpha: 0
        });
    }
}

function inverselerp(y, y0, y1) {
    return (y - y0) / (y1 - y0);
}

function computeAngularDistance(ray, point) {
    // Get the direction of the ray and the vector from the ray's origin to the point
    var rayDirection = ray.direction;
    var pointDirection = point.subtract(ray.origin).normalize(); // Direction from ray origin to point

    // Compute the angle between the two directions using the dot product
    var dotProduct = BABYLON.Vector3.Dot(rayDirection, pointDirection);

    // Clamp the value to prevent any precision errors
    dotProduct = Math.max(-1, Math.min(1, dotProduct));

    // Compute the angle (in radians) using arccosine of the dot product
    var angle = Math.acos(dotProduct);

    return angle; // Angle is in radians
}

function dummy_stars_data() {
    return [
        {
            hostname: "DBGSTAR-A",
            sy_dist: 10,
            ra: 0,
            dec: 0
        },
        {
            hostname: "DBGSTAR-B",
            sy_dist: 10,
            ra: 90,
            dec: 0
        },
    ];
}

function getRowData_sol() {
    return {
        hostname: "Sol",
        sy_dist: 0,
        glat: 0,
        glon: 0,
        st_spectype: "G2 V"
    }
}